import { useLoaderData } from 'react-router';

import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    DetailCreditsRow,
    type DetailCredit,
    type DetailCreditColumn,
} from '~/ui-rocco/pages/detail-shared';
import { PagesEnum } from '~/utils/route-config';

/**
 * Where a tag pill goes: the projects directory with the tag as its search
 * term. A stand-in until tags have somewhere of their own — free-text search
 * is the only route that takes an arbitrary tag today, and it does read them
 * (the directory shows the term in its search field and filters on it).
 */
const tagSearchLink = (tag: string) =>
    `${PagesEnum.PROJECTS}?search=${encodeURIComponent(tag)}`;

/**
 * The credit columns: the credited team and the suppliers whose products are
 * tagged into the project. Read by the row under the summary and, on the
 * phone, by the description that carries them behind Read more.
 */
export const useProjectCreditColumns = (): DetailCreditColumn[] => {
    const { project, projectTeamMembers, featuredProducts } =
        useLoaderData<typeof loader>();
    const { Professional } = project;
    const leadName = Professional.Title ?? '';

    // Build the credited team from the structured sources we have, deduped by
    // name: the lead professional, any project-group team members, and the
    // project's photographers.
    const credits: DetailCredit[] = [];
    const seen = new Set<string>();
    const addCredit = (
        name?: string | null,
        link?: string | null,
        role?: string | null,
    ) => {
        const trimmed = (name ?? '').trim();
        const key = trimmed.toLowerCase();
        if (!trimmed || seen.has(key)) return;
        seen.add(key);
        credits.push({
            name: trimmed,
            link: link || undefined,
            role: role?.trim() || undefined,
        });
    };

    addCredit(
        leadName,
        Professional.Link,
        Professional.Categories?.[0]?.PrettyTitle,
    );
    for (const group of projectTeamMembers ?? []) {
        for (const member of group.TeamMembers ?? []) {
            addCredit(
                member.ProfessionalName || member.Name,
                member.Link,
                group.Category,
            );
        }
    }
    for (const photographer of project.Photographers ?? []) {
        addCredit(photographer.Title, photographer.Link, 'Photographer');
    }

    // Every supplier whose product is tagged into this project, deduplicated:
    // a project commonly carries several products from the same maker, and the
    // column names makers, not products.
    const suppliers: DetailCredit[] = [];
    const seenSupplier = new Set<string>();
    for (const product of featuredProducts?.FeaturedProducts?.products ?? []) {
        const name = (product.professional?.name ?? '').trim();
        const key = name.toLowerCase();
        if (!name || seenSupplier.has(key)) continue;
        seenSupplier.add(key);
        suppliers.push({ name, link: product.professional?.link || undefined });
    }

    return [
        { label: 'Project team', entries: credits },
        {
            label: 'Product Suppliers',
            entries: suppliers,
            // Two columns once the list is long enough to need them;
            // a project with a handful of suppliers reads better as one.
            columns: suppliers.length > 5 ? 2 : 1,
        },
    ];
};

/**
 * P4 — meta row under the summary: the search tags, the credited team and
 * the suppliers whose products are tagged into the project, drawn by the
 * shared row the product and article pages use too.
 */
export const ProjectCreditsRow = ({
    hideMobileTagsLabel = false,
    showColumns = true,
}: {
    /** Set the tag pills without their label below md. */
    hideMobileTagsLabel?: boolean;
    /** Without the team and supplier columns: the tags alone. */
    showColumns?: boolean;
} = {}) => {
    const { project } = useLoaderData<typeof loader>();
    const leadName = project.Professional.Title ?? '';
    const tags = (project.SearchTags ?? [])
        .map((tag) => tag?.Text)
        .filter((text): text is string => !!text && text !== leadName)
        .map((label) => ({ label, to: tagSearchLink(label) }));
    const columns = useProjectCreditColumns();

    return (
        <DetailCreditsRow
            theme="dark"
            tags={tags}
            hideMobileTagsLabel={hideMobileTagsLabel}
            columns={showColumns ? columns : []}
        />
    );
};
